<template>
    <div class="h-full p-6 lg:p-12">
        <div v-if="!stepComplete || step.too_early">
            <div
                v-if="step?.too_early"
                class="rounded-lg bg-white p-6 shadow-lg mb-12"
            >
                <h3 class="text-lg xl:text-xl font-semibold leading-6 mb-3">
                    Existing Card Opened Too Early
                    <span class="fa-stack">
                        <i
                            class="fa-solid fa-circle fa-stack-2x text-red-100"
                        ></i>
                        <i
                            class="fa-regular fa-exclamation fa-stack-1x fa-inverse text-red-700"
                        ></i>
                    </span>
                </h3>
                <p class="text-base mb-3">
                    Unfortunately you opened your
                    {{ step?.accounts[0].card?.title }} ({{
                        step?.accounts[0].last_four
                    }}) just a bit too early to be able to earn the Companion
                    Pass for close to 2 years, which is what this tool provides
                    step-by-step instructions on how to complete.
                </p>
                <p class="text-base mb-3">
                    So you won't be able to use this tool, but not to fear. You
                    can still earn the Companion Pass for the remainder of 2024
                    and all of 2025. Just ensure that you'll earn 135,000 Rapid
                    Rewards points before the end of 2024, and you'll be all
                    set.
                </p>
            </div>

            <OpenCard
                :card="step?.cards[0]"
                :account="step?.accounts[0]"
                :too-early="step?.too_early"
                v-if="step?.too_early"
            />

            <div
                class="rounded-lg bg-white p-6 shadow-lg mb-12"
                v-if="!step?.too_early"
            >
                <h3 class="text-lg xl:text-xl font-semibold leading-6 mb-3">
                    Step 1: Open Your First Southwest Credit Card
                    <span class="fa-stack">
                        <i
                            class="fa-solid fa-circle fa-stack-2x text-orange-100"
                        ></i>
                        <i
                            class="fa-regular fa-clipboard-list fa-stack-1x fa-inverse text-orange-700"
                        ></i>
                    </span>
                </h3>
                <p class="text-base mb-3">
                    Your first step in earning your Companion Pass will be to
                    open a Southwest Credit Card.
                </p>
                <p class="text-base mb-3">
                    We suggest starting with one of the two Southwest Business
                    Cards:
                </p>
            </div>
            <div
                class="rounded-lg bg-white px-4 py-5 shadow-lg flex flex-row gap-6 items-center mb-3"
                v-if="card1 && !card1.hidden && !step?.too_early"
            >
                <div class="w-1/3">
                    <img
                        :src="`${cdnUrl}/${card1.card_image.path}/_small/${card1.card_image.file_name}`"
                        class="w-72"
                    />
                </div>
                <div class="w-2/3">
                    <h4 class="font-semibold mb-3">
                        {{ card1.title }}
                    </h4>
                    <div class="text-sm mb-3" v-html="card1.rich_text"></div>
                    <a
                        :href="card1.application_link"
                        class="btn btn-orange !ml-0 !text-md"
                        target="_blank"
                    >
                        Learn How to Apply
                        <i class="fa-light fa-arrow-right ml-2"></i>
                    </a>
                </div>
            </div>
            <div
                class="py-5 text-center mb-2"
                v-if="card1 && !card1.hidden && !step?.too_early"
            >
                <h3 class="font-semibold text-xl">Or</h3>
            </div>
            <div
                class="rounded-lg bg-white p-6 shadow-lg flex flex-row gap-6 items-center mb-3"
                v-if="card2 && !card1.hidden && !step?.too_early"
            >
                <div class="w-1/3">
                    <img
                        :src="`${cdnUrl}/${card2.card_image.path}/_small/${card2.card_image.file_name}`"
                        class="w-72"
                    />
                </div>
                <div class="w-2/3">
                    <h4 class="font-semibold mb-3">
                        {{ card2.title }}
                    </h4>
                    <div class="text-sm mb-3" v-html="card2.rich_text"></div>
                    <a
                        :href="card2.application_link"
                        class="btn btn-orange !ml-0 !text-md"
                        target="_blank"
                    >
                        Learn How to Apply
                        <i class="fa-light fa-arrow-right ml-2"></i>
                    </a>
                </div>
            </div>

            <div
                v-if="hasHiddenCard && !step?.too_early"
                class="text-center absolute bottom-1 m-auto left-0 right-0"
            >
                <button
                    @click="showHidden()"
                    class="btn btn-secondary !w-24 md:!w-48 !bg-gray-50"
                >
                    <i class="fa-regular fa-eye mr-2"></i>
                    Show Hidden Card
                </button>
            </div>
        </div>
        <div v-if="stepComplete && !step?.too_early">
            <div class="rounded-lg bg-white p-6 shadow-lg mb-12">
                <h3 class="text-lg xl:text-xl font-semibold leading-6 mb-3">
                    Step 1 Complete
                    <span class="fa-stack">
                        <i
                            class="fa-solid fa-circle fa-stack-2x text-green-100"
                        ></i>
                        <i
                            class="fa-regular fa-check fa-stack-1x fa-inverse text-green-700"
                        ></i>
                    </span>
                </h3>
                <p class="text-base mb-3">
                    You've taken the first step in earning your Companion Pass
                    by opening your
                    <span class="font-semibold">
                        {{ step?.cards[0].title }}.
                    </span>
                </p>
            </div>
            <OpenCard :card="step?.cards[0]" :account="step?.accounts[0]" />
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useCompanionPassStore } from '@/store/companionpass';

import OpenCard from '@/components/companion-pass/cards/open-card.vue';

export default {
    name: 'StepOne',
    props: {},
    components: {
        OpenCard,
    },
    data: () => ({}),
    computed: {
        ...mapState(useCompanionPassStore, ['completeSteps']),
        stepContent() {
            return this.getStepContent(1);
        },
        step() {
            return this.getStep(1);
        },
        stepComplete() {
            return 1 in this.completeSteps ? true : false;
        },
        card1() {
            return this.stepContent?.business_cards[0];
        },
        card2() {
            return this.stepContent?.business_cards[1];
        },
        hasHiddenCard() {
            let hidden = false;
            if (this.card1 && this.card1.hidden) {
                hidden = true;
            }
            if (this.card2 && this.card2.hidden) {
                hidden = true;
            }
            return hidden;
        },
        cdnUrl() {
            return window.cdnUrl;
        },
    },
    methods: {
        ...mapActions(useCompanionPassStore, ['getStep', 'getStepContent']),
        showHidden() {
            if (this.card1) {
                this.card1.hidden = false;
            }
            if (this.card2) {
                this.card2.hidden = false;
            }
        },
    },
    mounted() {},
};
</script>
